<template>
  <div
    class="order-card-wrap background-white rounded-regular shadow-light px-3 py-4"
  >
    <div class="heading d-flex flex-column flex-lg-row justify-content-lg-between align-items-center">
      <div class="d-flex flex-column flex-md-row justify-content-center justify-content-md-between text-center text-md-left align-items-center col-lg-9 px-0 px-lg-3">
        <span class="text-color2 font-weight-bold font-size-medium mr-3 mr-lg-4">
          {{ order.created_at | formatDate('DD/MM') }}
        </span>
        <span class="mt-3 mt-md-0">
          <label class="font-weight-bold d-block font-size-small mb-0 mr-md-3 mr-lg-4">Valor pago</label>
          <span v-if="order.is_scriv_pro">Scriv Pro</span>
          <span v-else>{{ (order.amount/100) | formatMoney }}</span>
        </span>
        <span class="mt-3 mt-md-0">
          <label class="font-weight-bold d-block font-size-small mb-0">Número do Pedido</label>
          {{ order.id }}
        </span>
      </div>
      <div
        class="mt-4 mt-lg-0"
        v-if="expandable"
      >
        <Button
          class="btn-outline-color2"
          @click="open"
          v-if="!isOpen"
        >
          Ver detalhes
        </Button>
        <Button
          class="btn-outline-color2"
          @click="close"
          v-else
        >
          Fechar
        </Button>
      </div>
    </div>
    <div
      class="mx-auto col-md-10"
      v-if="isOpen"
    >
      <hr class="h-1 background-color2 my-4" />

      <template
        v-if="loader"
      >
        <Loader
          color="color2"
          size="big"
        />
      </template>
      <template v-else-if="fullOrder">
        <h2 class="font-weight-bold text-color2 font-size-medium">
          Produtos
        </h2>
        <ul>
          <li
            v-for="(item, index) in fullOrder.items"
            :key="index"
          >
            <span
              v-if="item.type === 'book'"
            >
              <strong>Livro</strong> -
              <router-link
                :to="{ name: 'books.show', params: { username: item.entity.user.username, slug: item.entity.slug } }"
                class="link-color2"
              >
                {{ item.entity.title }} ({{ item.entity.user.name }}) {{ fullOrder.is_scriv_pro ? '- (Scriv Pro)' : '' }}
              </router-link>
            </span>
            <span
              v-else-if="item.type === 'subscription'"
            >
              <strong>Assinatura</strong> -
              <router-link
                :to="{ name: 'groups.show', params: { slug: item.entity.entity.slug } }"
                class="link-color2"
              >
                {{ item.entity.entity.name }}
              </router-link>
            </span>
            <span
              v-if="item.type === 'donation'"
            >
              <strong>Cafezinho para</strong> -
              <router-link
                :to="{ name: 'users.show', params: { username: item.entity.username } }"
                class="link-color2"
              >
                {{ item.entity.name }} ({{ item.entity.username }})
              </router-link>
            </span>
          </li>
        </ul>

        <h2 class="font-weight-bold text-color2 font-size-medium" v-if="!fullOrder.is_scriv_pro">
          Cobranças
        </h2>
        <template v-if="!fullOrder.is_scriv_pro">
          <div
            class="mt-3"
            v-for="invoice in fullOrder.invoices"
            :key="invoice.id"
          >
            <!-- TRY AGAIN MESSAGE -->
            <template v-if="invoice.payment_status === 'refused'">
              <div class="text-center col-md-8 mx-auto">
                <BaseIcon
                    size="medium"
                  >
                    <WarningIcon />
                  </BaseIcon>
                <p>Infelizmente seu pagamento foi recusado. Que tal tentar novamente com outro meio de pagamento?</p>
              </div>
            </template> <!-- END TRY AGAIN -->
            <h3 class="font-weight-bold text-color2 font-size-regular">Cobrança {{ invoice.id }}</h3>
            <ul class="pl-3">
              <li>
                <strong>Data:</strong> {{ invoice.created_at | formatDate('DD/MM/YYYY') }}
              </li>
              <li>
                <strong>Valor:</strong> {{ invoice.amount / 100 | formatMoney }}
              </li>
              <li>
                <strong>Status do pagamento:</strong> {{ invoice.payment_status | formatPaymentStatus }}
              </li>
              <li>
                <strong>Forma de pagamento:</strong> {{ invoice.payment_method | formatPaymentMethod }}
              </li>
              <template v-if="invoice.payment_method === 'credit_card' && invoice.card">
                <li>
                  <strong>Bandeira do cartão:</strong> {{ invoice.card.brand | formatCreditCardBrand }}
                </li>
                <li>
                  <strong>Cartão de final:</strong> {{ invoice.card.last_digits }}
                </li>
              </template>
              <template v-else-if="invoice.payment_method === 'boleto' && invoice.payment_status === 'waiting_payment'">
                <li>
                  <strong>Código de barras:</strong> {{ invoice.boleto_barcode }}
                </li>
                <li>
                  <strong>URL do boleto:</strong> <a :href="invoice.boleto_url" class="link-color2" target="_blank">{{ invoice.boleto_url }}</a>
                </li>
                <li>
                  <strong>Data de vencimento:</strong> {{ invoice.boleto_expiration_date | formatDate('DD/MM/YYYY HH:mm:ss') }}
                </li>
              </template>
              <template v-else-if="invoice.payment_method === 'pix' && invoice.payment_status === 'waiting_payment'">
                <li>
                  <div class="col-md-8 mx-auto mt-3">
                    <QRCodeCard
                      :code="invoice.pix_qr_code"
                    />
                  </div>
                </li>
              </template>
            </ul>
            <h3 class="font-weight-bold font-size-regular text-color2 pl-3">Histórico da transação</h3>
            <ul class="pl-3">
              <li
                v-for="log in invoice.payment_logs"
                :key="log.id"
              >
                <strong>Status:</strong> {{ log.status | formatPaymentStatus }} ({{ log.created_at | formatDate('DD/MM/YYYY HH:mm') }})
              </li>
            </ul>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>

import OrdersService from '@/modules/orders/services/orders-service'

import BaseIcon from '@/components/common/Icon/BaseIcon'
import Button from '@/components/common/Button/Button'
import Loader from '@/components/common/Loader/Loader'
import QRCodeCard from '@/components/common/QRCode/QRCodeCard'
import WarningIcon from '@/components/common/Icon/WarningIcon'

export default {
  name: 'OrderCard',
  props: ['order', 'expandable'],
  components: {
    BaseIcon,
    Button,
    Loader,
    QRCodeCard,
    WarningIcon
  },
  data () {
    return {
      loader: false,
      isOpen: false,
      fullOrder: null
    }
  },
  methods: {
    open () {
      this.isOpen = true
      if (!this.fullOrder) this.getOrder()
    },
    close () {
      this.isOpen = false
    },
    async getOrder () {
      this.loader = true
      try {
        const order = await OrdersService.getOrder(this.order.id)
        this.fullOrder = Object.assign({}, order)
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    }
  }
}
</script>
